import { useCallback, useEffect, useRef, useState } from 'react';

// Keep a floating card beside the thing it is about: clamp it into the viewport
// and flip it below its anchor when there is no room above (T403,
// Planning/GANTT-DRAG-CONFIRM-2026-08-30.md §8.3).
//
// ⛔ THIS ARITHMETIC EXISTED TWICE AND WAS ABOUT TO EXIST THREE TIMES - once in
// GanttChart, once in TemplateGantt, and again in the drag confirmation. A third
// copy is a third thing to drift, and the case that would drift silently is the
// FLIP, which is exactly the geometry §7.4 warns about: near the top of the
// viewport the card lands where the finger just was.
//
// ⛔ IT MEASURES, SO IT CANNOT ANSWER BEFORE IT HAS RENDERED. `placed` is null
// on the first pass and the caller draws hidden until it arrives - the
// `visibility: tooltipPos ? 'visible' : 'hidden'` that GanttPopups.js:181
// already keys on. That is not a loading state to design away: a card cannot be
// clamped to a width nobody has measured yet.
//
// anchor  { x, y } | null - VIEWPORT coordinates. y is the anchored element's
//                           top minus 8, x is the pointer (see §7.3: a bar
//                           zoomed wider than the screen has its midpoint off
//                           it, so the midpoint is the wrong x).
// ref                     - goes on the card, so there is something to measure.
// placed  { x, y, arrowPosition } | null
// clear                   - drops the measurement without touching the anchor.
export function useAnchoredPlacement(anchor) {
    const ref = useRef(null);
    const [placed, setPlaced] = useState(null);

    useEffect(() => {
        if (!anchor || !ref.current) {
            // ⚠️ Clearing here is what removed the callers' own
            // `setTooltipPos(null)` lines. A stale placement outliving its
            // anchor is a card that reappears in last week's position.
            setPlaced(null);
            return;
        }
        const rect = ref.current.getBoundingClientRect();
        const padding = 8;

        let adjustedX = anchor.x;
        let adjustedY = anchor.y;
        let arrowPosition = 'bottom'; // Arrow points down by default (card above)

        // Horizontal bounds
        const halfWidth = rect.width / 2;
        if (anchor.x - halfWidth < padding) {
            adjustedX = halfWidth + padding;
        } else if (anchor.x + halfWidth > window.innerWidth - padding) {
            adjustedX = window.innerWidth - halfWidth - padding;
        }

        // Vertical bounds - no room above, so flip below
        if (anchor.y - rect.height < padding) {
            // anchor.y is 8px above the element's top and a bar is ~24px tall,
            // so its bottom is about anchor.y + 32.
            adjustedY = anchor.y + 40;
            arrowPosition = 'top';
        }

        // ...and do not fall off the bottom once flipped
        if (adjustedY > window.innerHeight - padding) {
            adjustedY = window.innerHeight - padding;
        }

        setPlaced({ x: adjustedX, y: adjustedY, arrowPosition });
    }, [anchor]);

    const clear = useCallback(() => setPlaced(null), []);

    return { ref, placed, clear };
}

export default useAnchoredPlacement;
